/**
 * @fileoverview Search Facets Implementation
 * @description Implements a dynamic search facets system for filtering products
 *
 * Features:
 * - Dynamic facet filtering
 * - Price range filtering
 * - Active facet management
 * - URL hash synchronization
 * - Debounced form submission
 * - Cache management for search results
 *
 * HTML Structure Required:
 * - Form element with id "FacetFiltersForm"
 * - Elements with ids:
 *   - FacetsWrapperDesktop
 *   - ProductGridContainer
 *   - ProductCount
 *   - ProductCountDesktop
 *   - filterRest
 *   - resultCount
 */

/**
 * @class FacetFiltersForm
 * @extends HTMLElement
 * @description Main class for handling facet filters functionality
 */
class FacetFiltersForm extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the facet filters form
   * - Sets up event listeners
   * - Configures debounced form submission
   * - Initializes page rendering
   */
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // const searchParams = localStorage.getItem('searchParams');
    // searchParams.replaceAll('?', '')
    // FacetFiltersForm.renderPage(url);

    FacetFiltersForm.renderPage(window.location.search.replaceAll('?', ''));

    // FacetFiltersForm.renderPage(window.location.search, null, false);
  }

  /**
   * @static
   * @function setListeners
   * @description Sets up history change listener for browser navigation
   */
  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  /**
   * @static
   * @function toggleActiveFacets
   * @description Toggles the disabled state of facet remove buttons
   * @param {boolean} disable - Whether to disable the facets
   */
  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /**
   * @static
   * @function renderPage
   * @description Renders the page with updated search parameters
   * @param {string} searchParams - The search parameters to apply
   * @param {Event} event - The triggering event
   * @param {boolean} updateURLHash - Whether to update the URL hash
   */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById(
      'ProductCountDesktop'
    );
    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  /**
   * @static
   * @function renderSectionFromFetch
   * @description Fetches and renders a section from the server
   * @param {string} url - The URL to fetch from
   * @param {Event} event - The triggering event
   */
  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  /**
   * @static
   * @function renderSectionFromCache
   * @description Renders a section from the cache
   * @param {Function} filterDataUrl - Function to find cached data
   * @param {Event} event - The triggering event
   */
  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  /**
   * @static
   * @function renderProductGridContainer
   * @description Updates the product grid container with new content
   * @param {string} html - The HTML content to render
   */
  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    if (
      document.querySelector('.js-load-more') ||
      document.querySelector('.js-infinite-scroll')
    )
      loadMore();
    try {
      colorSwatches();
    } catch (err) {}
  }

  /**
   * @static
   * @function renderProductCount
   * @description Updates the product count display
   * @param {string} html - The HTML content containing count information
   */
  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count.innerHTML;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    let strCount = count.querySelector('.applied_count').innerHTML;

    setTimeout(() => {
      let searchData = new URLSearchParams(window.location.search);
      let appliedCount = 0;

      searchData.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          appliedCount++;
        }
      });

      if (appliedCount > 0) {
        document.getElementById('filterRest').style.display = 'block';
        if (appliedCount == 1) {
          document
            .getElementById('ProductCount')
            .querySelector(
              '.applied_count'
            ).innerHTML = `${strCount} (${appliedCount} Filter Applied)`;
        } else {
          document
            .getElementById('ProductCount')
            .querySelector(
              '.applied_count'
            ).innerHTML = `${strCount} (${appliedCount} Filters Applied)`;
        }
      } else {
        document.getElementById('filterRest').style.display = 'none';
      }

      document.querySelectorAll('.open_filters').forEach((filter) => {
        if (filter) {
          if (appliedCount > 0) {
            filter.querySelector(
              '.filter-menu-text'
            ).innerHTML = `Filter (${appliedCount})`;
          } else {
            filter.querySelector('.filter-menu-text').innerHTML = `Filter`;
          }
        }
      });
    }, 200);

    document.getElementById('resultCount').innerHTML = `(${strCount
      .replace('Item', '')
      .replace('s', '')
      .trim()})`;
  }

  /**
   * @static
   * @function renderFilters
   * @description Updates the filter elements with new content
   * @param {string} html - The HTML content to render
   * @param {Event} event - The triggering event
   */
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest('.js-filter')
      );
  }

  /**
   * @static
   * @function renderActiveFacets
   * @description Updates the active facets display
   * @param {string} html - The HTML content containing active facets
   */
  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * @static
   * @function renderAdditionalElements
   * @description Updates additional elements with new content
   * @param {string} html - The HTML content to render
   */
  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    //document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  /**
   * @static
   * @function renderCounts
   * @description Updates the count displays for filters
   * @param {HTMLElement} source - The source element containing counts
   * @param {HTMLElement} target - The target element to update
   */
  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML =
        source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML =
        source.querySelector('.facets__summary').outerHTML;
    }
  }

  /**
   * @static
   * @function updateURLHash
   * @description Updates the URL hash with search parameters
   * @param {string} searchParams - The search parameters to set
   */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  /**
   * @static
   * @function getSections
   * @description Gets the sections to update
   * @returns {Array} Array of section information
   */
  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  /**
   * @function createSearchParams
   * @description Creates search parameters from form data
   * @param {HTMLFormElement} form - The form element
   * @returns {string} The search parameters string
   */
  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /**
   * @function onSubmitForm
   * @description Handles form submission
   * @param {string} searchParams - The search parameters
   * @param {Event} event - The form submit event
   */
  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  /**
   * @function onSubmitHandler
   * @description Handles form input changes
   * @param {Event} event - The input event
   */
  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form'
    );
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form')
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  /**
   * @function onActiveFilterClick
   * @description Handles clicks on active filter remove buttons
   * @param {Event} event - The click event
   */
  onActiveFilterClick(event) {
    event.preventDefault();

    this.querySelectorAll('.facets__details__wrapper').forEach((wrapper) => {
      wrapper.querySelectorAll('input').forEach((input) => {
        if (input.checked) {
          input.click();
        }
      });
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * @class PriceRange
 * @extends HTMLElement
 * @description Handles price range filter functionality
 */
class PriceRange extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the price range filter
   * - Sets up range input references
   * - Configures initial values
   * - Sets up event listeners
   */
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  /**
   * @function onRangeChange
   * @description Handles range input changes
   * @param {Event} event - The input change event
   */
  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  /**
   * @function setMinAndMaxValues
   * @description Sets the min and max values for the range inputs
   */
  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  /**
   * @function adjustToValidValues
   * @description Adjusts input values to valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  /**
   * @function fillSlider
   * @description Updates the slider fill based on current values
   */
  fillSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    inputRangeWrappers.forEach((inputWrapper) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = inputRangeTo.max - inputRangeTo.min;
      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute(
        'style',
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    });
  }

  /**
   * @function controlSlider
   * @description Handles slider control interactions
   */
  controlSlider() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    const inputNumberWrappers = document.querySelectorAll(
      '.facets__price .facets__price-wrapper'
    );

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom =
        inputNumberWrappers[index].querySelectorAll('.field__input')[0];
      const inputNumberTo =
        inputNumberWrappers[index].querySelectorAll('.field__input')[1];

      inputRangeFrom.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll(
      '.facets__price .facets__range'
    );
    const inputNumberWrappers = document.querySelectorAll(
      '.facets__price .facets__price-wrapper'
    );

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll('.field__input');
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom =
        inputRangeWrappers[index].querySelectorAll('.field__range')[0];
      const inputRangeTo =
        inputRangeWrappers[index].querySelectorAll('.field__range')[1];

      inputNumberFrom.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
        }

        this.fillSlider();
      };
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
